import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HourlyProject, TimeEntryHourly } from '../../interfaces/EmployeeInterface';
import { AccountCode } from '../../interfaces/AccountCodeInterface';
import { TimeEntryType } from '../../interfaces/TimesheetInterface';
import {
  PayrollEntryResult, PayrollHourlyEntryBody,
} from '../../interfaces/TimesheetEditInterface';
import { ProjectPhaseWithAccountCodes } from '../../interfaces/ProjectCompleteInterface';
import ServiceOrdersAPI from '../../api/serviceOrders';
import {
  instantToLocalWallTime, parseClock, PUNCH_CROSSES_MIDNIGHT_CODE, PUNCH_CROSSES_MIDNIGHT_MESSAGE,
  resolveOutMinutes, wallTimeToTenantInstant, ymdLabel,
} from './clockParse';
import { getAccountCodesCached, getCompleteProjectCached } from './lookups';
import ProjectPicker, { PickedProject } from './ProjectPicker';
import ServiceOrderPicker, { PickedServiceOrder, toPickedServiceOrder } from './ServiceOrderPicker';
import { HourlyMode, HourlyRowShape, resolveHourlyModeConfig } from './hourlyModes';
import { EntryErrorBanner, EntryModalShell, performEntrySave, PunchErrorSlot } from './EntryModalShell';
import { PunchEchoParts } from './echoParts';
import config from '../../configLoader';
import { describeNpmServiceOrder } from '../../util/serviceOrderDisplay';

/**
 * Hourly punch editor (M2-B, design shot 06). In/out are the 10-key clock
 * fields; project → phase → account cascade from the complete-project read;
 * "No job — bare hours" selects the bare punch (project/phase/CSW sent as the
 * contract's 0 sentinel — §8.1 requireInt). Type REG/MEAL.
 *
 * A punch whose wall time did not move is sent with its ORIGINAL instant and
 * offset verbatim, so a no-op save never relabels a device punch as a manual
 * edit (the server stamps PunchType 3 per punch only when its time moved).
 *
 * 2020PH/NV (config `clockHourlyV2Config` present) — the modal splits into
 * modes: Job (project + phase + cost code, all required), Non-Job Time (the
 * configured account code, locked and hidden) and Service Order (SO + a cost
 * code from the SO job's phases). When the block is ABSENT (`mode === null`,
 * WCDP/NPM/Wallcraft) the modal renders and saves exactly as it always has.
 */

const MODE_LABEL: Record<HourlyMode, string> = {
  job: 'Job',
  nonJob: 'Non-Job Time',
  serviceOrder: 'Service Order',
};

type BadField = 'in' | 'out' | 'project' | 'phase' | 'account' | 'serviceOrder';

interface CodeOption { accountCodeID: number; alt: string; name: string; }

export interface HourlyEntryTarget {
  row: TimeEntryHourly;
  group: HourlyProject;
}

export interface HourlyCarry {
  /** The 2020 hourly mode the Enter loop stays in; null on legacy tenants. */
  mode: HourlyMode | null;
  project: PickedProject | null;
  projectPhaseID: number | null;
  accountCodeID: number | null;
  serviceOrder: PickedServiceOrder | null;
  type: number;
}

export interface HourlyEntryModalProps {
  employeeID: number;
  payPeriodID: number;
  ymd: string;
  employeeName: string;
  editSessionID: string;
  entry: HourlyEntryTarget | null;
  carry: HourlyCarry | null;
  /** The row's classified shape (edit only) — picks the 2020 mode the modal opens in. */
  initialShape?: HourlyRowShape;
  onSaved: (result: PayrollEntryResult, close: boolean, carry: HourlyCarry) => void;
  onCancel: () => void;
  onConflict: (message: string) => void;
}

const HourlyEntryModal: React.FC<HourlyEntryModalProps> = ({
  employeeID, payPeriodID, ymd, employeeName, editSessionID,
  entry, carry, initialShape, onSaved, onCancel, onConflict,
}) => {
  const isEdit = entry != null;
  const cfg = useMemo(() => resolveHourlyModeConfig(), []);
  /**
   * null = LEGACY (no clockHourlyV2Config): today's free-form modal, untouched.
   * Edit opens in the row's shape; a Non-Job row on a tenant without that mode
   * opens in Job with its account kept and a "This punch has no job" warning.
   * Add resumes the loop's mode, else the first configured one.
   */
  const orphanNonJob = cfg.present && isEdit && initialShape === 'nonJob'
    && !cfg.modes.includes('nonJob') && cfg.modes.includes('job');
  const initialMode: HourlyMode | null = !cfg.present
    ? null
    : isEdit
      ? (orphanNonJob
        ? 'job'
        : isHourlyMode(initialShape) && cfg.modes.includes(initialShape)
          ? initialShape
          : cfg.modes[0] ?? null)
      : (carry?.mode && cfg.modes.includes(carry.mode) ? carry.mode : cfg.modes[0] ?? null);
  /**
   * TAS-514 — the punch's service order + scope, shown READ ONLY.
   *
   * There is deliberately no lookup or edit control here. Only the employee resolves a
   * temporary service order, on mobile (locked decision 3), and there is no admin-side
   * lookup endpoint to resolve one against anyway: `GET /serviceOrder/lookup` sits behind
   * `validateFirebaseTokenWithEmployee` and is not callable with an admin token. So the
   * editor's whole job here is to SHOW the link and not drop it on save.
   *
   * Gated on the `serviceOrders` entry capability, which is true only on the two NPM v2
   * configs — the three tenants that had it stale-true (2020PH, 2020NV, SBytes) were set
   * to false when this became the flag's first consumer, so nothing renders for them that
   * did not render before.
   *
   * NOT the 2020 Service Order MODE below: that mode is gated by
   * `clockHourlyV2Config.enabledPayloadKinds` ('ServiceOrder') only, and
   * `entryCapabilities.serviceOrders` must stay false on 2020PH/NV so this NPM block
   * never renders there.
   */
  const serviceOrder = config.payrollTimesheetEditing?.entryCapabilities?.serviceOrders
    ? describeNpmServiceOrder(entry?.group, entry?.row)
    : null;
  const origIn = isEdit
    ? instantToLocalWallTime(entry!.row.inDateTime, entry!.row.inDateTimeOffset)
    : null;
  const origOut = isEdit && entry!.row.outDateTime
    ? instantToLocalWallTime(entry!.row.outDateTime, entry!.row.outDateTimeOffset)
    : null;
  /** Edit keeps the punch's own local date; add uses the day card's date. */
  const baseYmd = origIn?.ymd ?? ymd;

  const initialProject: PickedProject | null = isEdit
    ? (entry!.group.project?.projectID == null
      ? null
      : {
        projectID: entry!.group.project.projectID,
        altProjectID: entry!.group.project.altProjectID ?? '',
        name: entry!.group.project.name ?? '',
      })
    : carry?.project ?? null;
  const initialPhaseID: number | null =
    isEdit ? entry!.group.projectPhase?.projectPhaseID ?? null : carry?.projectPhaseID ?? null;
  const initialAccountCodeID: number | null =
    isEdit ? entry!.group.account?.accountCodeID ?? null : carry?.accountCodeID ?? null;

  // 2020 modes: an add inherits the carry only when it stays in the carried mode.
  const carryFits = !isEdit && carry != null && carry.mode === initialMode;
  const initialServiceOrder: PickedServiceOrder | null = initialMode !== 'serviceOrder'
    ? null
    : isEdit
      ? (entry!.row.serviceOrder
        ? {
          serviceOrderID: entry!.row.serviceOrderID ?? entry!.row.serviceOrder.serviceOrderID ?? 0,
          altServiceOrderID: entry!.row.serviceOrder.altServiceOrderID,
          name: entry!.row.serviceOrder.name,
          projectID: entry!.row.serviceOrder.projectID ?? entry!.group.project?.projectID ?? null,
          altProjectID: entry!.group.project?.altProjectID ?? null,
          projectName: entry!.group.project?.name ?? null,
          // Confirmed (closed job or not) by the detail read on mount.
          projectActive: true,
        }
        : null)
      : (carryFits ? carry?.serviceOrder ?? null : null);

  const [mode, setMode] = useState<HourlyMode | null>(initialMode);
  const [project, setProject] = useState<PickedProject | null>(
    initialMode === null
      ? initialProject
      : initialMode === 'job' && (isEdit || carryFits) ? initialProject : null
  );
  const [phaseID, setPhaseID] = useState<number | null>(
    initialMode === null
      ? initialPhaseID
      : initialMode === 'job' && (isEdit || carryFits) ? initialPhaseID : null
  );
  const [accountCodeID, setAccountCodeID] = useState<number | null>(
    initialMode === null
      ? initialAccountCodeID
      : initialMode === 'nonJob'
        ? cfg.nonJobAccountCodeID
        : isEdit || carryFits ? initialAccountCodeID : null
  );
  const [type, setType] = useState<number>(isEdit ? entry!.row.type : carry?.type ?? TimeEntryType.Regular);
  const [inRaw, setInRaw] = useState<string>(origIn ? clockText(origIn.minutes) : '');
  const [outRaw, setOutRaw] = useState<string>(origOut ? clockText(rolled(origOut, origIn)) : '');
  const [phases, setPhases] = useState<ProjectPhaseWithAccountCodes[] | null>(null);
  const [allAccounts, setAllAccounts] = useState<AccountCode[]>([]);
  const [pickedSO, setPickedSO] = useState<PickedServiceOrder | null>(initialServiceOrder);
  /** The SO job's cost codes; null until the detail read lands. */
  const [soCodes, setSoCodes] = useState<CodeOption[] | null>(null);
  /** Editing an SO punch whose service order is not in the synced table. */
  const [soNotInSync, setSoNotInSync] = useState(false);
  const [bad, setBad] = useState<BadField | null>(null);
  const [banner, setBanner] = useState<{ title: string; body?: string } | null>(null);
  const [saving, setSaving] = useState(false);
  /** Cross-midnight refusal in the fixed slot under In / Out (client check or the API's 400). */
  const [punchErr, setPunchErr] = useState<string | null>(null);
  const inRef = useRef<HTMLInputElement>(null);
  const outRef = useRef<HTMLInputElement>(null);
  const soSeq = useRef(0);

  useEffect(() => {
    getAccountCodesCached().then(setAllAccounts).catch(() => setAllAccounts([]));
  }, []);

  // Project → phases (+ their account codes). Defaults pick the first phase /
  // account only when nothing is selected, so carried values survive the loop.
  // 2020: Job mode only — never in Service Order / Non-Job Time.
  useEffect(() => {
    let alive = true;
    if (mode !== null && mode !== 'job') { setPhases(null); return undefined; }
    if (!project) { setPhases(null); return undefined; }
    getCompleteProjectCached(project.projectID)
      .then((complete) => {
        if (!alive) return;
        const ph = complete.projectPhasesAccountCodes ?? [];
        setPhases(ph);
        if (ph.length > 0) {
          setPhaseID((current) => {
            const chosen = ph.find((p) => p.projectPhaseID === current) ?? ph[0];
            setAccountCodeID((acct) => {
              const codes = chosen.accountCodes ?? [];
              return codes.some((c) => c.accountCodeID === acct) ? acct : (codes[0]?.accountCodeID ?? acct);
            });
            return chosen.projectPhaseID;
          });
        }
      })
      .catch(() => { if (alive) setPhases([]); });
    return () => { alive = false; };
  }, [project, mode]);

  /**
   * Service Order mode — the SO job's cost codes (`GET admin/serviceOrders/:id`).
   * When the edited punch's SO cannot be read and never synced, the options fall
   * back to the codes across its job's phases and the save keeps the punch's
   * SO/project IDs verbatim.
   */
  const loadServiceOrder = (serviceOrderID: number, fromRow: boolean) => {
    const seq = ++soSeq.current;
    setSoCodes(null);
    ServiceOrdersAPI.getServiceOrder(serviceOrderID)
      .then((so) => {
        if (seq !== soSeq.current) return;
        const codes = dedupeCodes((so.accountCodes ?? []).map((c) => ({
          accountCodeID: c.accountCodeID, alt: c.altAccountCodeID, name: c.name,
        })));
        setPickedSO(toPickedServiceOrder(so));
        setSoCodes(codes);
        setAccountCodeID((acct) => (codes.some((c) => c.accountCodeID === acct)
          ? acct
          : codes.length === 1 ? codes[0].accountCodeID : null));
      })
      .catch(() => {
        if (seq !== soSeq.current) return;
        if (fromRow && entry != null && entry.row.serviceOrder == null) setSoNotInSync(true);
        const projectID = fromRow
          ? entry?.row.serviceOrder?.projectID ?? entry?.group.project?.projectID ?? null
          : null;
        if (projectID == null) { setSoCodes([]); return; }
        getCompleteProjectCached(projectID)
          .then((complete) => {
            if (seq !== soSeq.current) return;
            setSoCodes(dedupeCodes((complete.projectPhasesAccountCodes ?? []).flatMap((ph) =>
              (ph.accountCodes ?? []).map((c) => ({
                accountCodeID: c.accountCodeID, alt: c.altAccountCodeID, name: c.name,
              }))
            )));
          })
          .catch(() => { if (seq === soSeq.current) setSoCodes([]); });
      });
  };

  // An SO punch being edited (or a carried SO) → resolve it once on mount.
  useEffect(() => {
    if (initialMode !== 'serviceOrder') return;
    const id = isEdit
      ? entry!.row.serviceOrderID ?? entry!.row.serviceOrder?.serviceOrderID ?? null
      : initialServiceOrder?.serviceOrderID ?? null;
    if (id != null) loadServiceOrder(id, isEdit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchMode = (next: HourlyMode) => {
    if (next === mode) return;
    soSeq.current += 1; // drop any in-flight SO read
    setMode(next);
    setPhaseID(null);
    setAccountCodeID(next === 'nonJob' ? cfg.nonJobAccountCodeID : null);
    // The project survives only into Job (an SO's job becomes the Job pick).
    setProject(next === 'job'
      ? project ?? (pickedSO?.projectID != null
        ? { projectID: pickedSO.projectID, altProjectID: pickedSO.altProjectID ?? '', name: pickedSO.projectName ?? '' }
        : null)
      : null);
    setPickedSO(null);
    setSoCodes(null);
    setSoNotInSync(false);
    setBad(null);
    setBanner(null);
  };

  const phase = useMemo(
    () => (phases ?? []).find((p) => p.projectPhaseID === phaseID) ?? null,
    [phases, phaseID]
  );

  // Account options: the selected phase's codes when the project publishes
  // phases; otherwise (bare punch, or a project with no phase data) all codes.
  const accountOptions = useMemo(() => {
    if (project && phase && (phase.accountCodes ?? []).length > 0) {
      return phase.accountCodes.map((c) => ({
        accountCodeID: c.accountCodeID, alt: c.altAccountCodeID, name: c.name,
      }));
    }
    return allAccounts.map((c) => ({
      accountCodeID: c.accountCodeID, alt: c.altAccountCodeID, name: c.name,
    }));
  }, [project, phase, allAccounts]);

  /** 2020 Job mode: the selected phase's codes only. */
  const jobAccountOptions: CodeOption[] = (phase?.accountCodes ?? []).map((c) => ({
    accountCodeID: c.accountCodeID, alt: c.altAccountCodeID, name: c.name,
  }));
  const jobHasNoPhases = mode === 'job' && project != null && phases != null && phases.length === 0;
  const soHasNoCodes = mode === 'serviceOrder' && soCodes != null && soCodes.length === 0
    && (pickedSO != null || soNotInSync);
  const soJobClosed = mode === 'serviceOrder' && isEdit && pickedSO?.projectActive === false;

  const markBad = (which: 'in' | 'out') => {
    setBad(which);
    (which === 'in' ? inRef : outRef).current?.focus();
  };

  const markRequired = (which: BadField, title: string) => {
    setBad(which);
    setBanner({ title });
  };

  const clearBad = (which: BadField) => {
    if (bad === which) { setBad(null); setBanner(null); }
  };

  /** The job-costing half of a 2020 body (0 is the contract's "none" sentinel). */
  const modeFields = (): Pick<PayrollHourlyEntryBody,
    'projectID' | 'projectPhaseID' | 'accountCodeID' | 'serviceOrderText' | 'serviceOrderID'
    | 'serviceOrderScopeID' | 'tempServiceOrderDescription'> => {
    if (mode === 'nonJob') {
      return {
        projectID: 0,
        projectPhaseID: 0,
        accountCodeID: cfg.nonJobAccountCodeID ?? 0,
        serviceOrderText: '',
        serviceOrderID: null,
        serviceOrderScopeID: null,
        tempServiceOrderDescription: null,
      };
    }
    if (mode === 'serviceOrder') {
      // Not-in-sync edit: the punch's SO and project IDs go back verbatim.
      const serviceOrderID = pickedSO?.serviceOrderID
        ?? (soNotInSync ? entry?.row.serviceOrderID ?? null : null);
      const projectID = pickedSO
        ? pickedSO.projectID ?? 0
        : entry?.row.projectID ?? entry?.group.project?.projectID ?? 0;
      return {
        projectID,
        projectPhaseID: 0,
        accountCodeID: accountCodeID ?? 0,
        serviceOrderText: entry?.group.serviceOrderText ?? '',
        serviceOrderID,
        serviceOrderScopeID: null,
        tempServiceOrderDescription: null,
      };
    }
    return {
      projectID: project?.projectID ?? 0,
      projectPhaseID: phaseID ?? 0,
      accountCodeID: accountCodeID ?? 0,
      serviceOrderText: '',
      serviceOrderID: null,
      serviceOrderScopeID: null,
      tempServiceOrderDescription: null,
    };
  };

  const handleSave = async (close: boolean) => {
    const i = parseClock(inRaw);
    if (i == null) { markBad('in'); return; }
    let outMinutes: number | null = null;
    if (outRaw.trim() !== '') {
      const o = parseClock(outRaw);
      if (o == null) { markBad('out'); return; }
      // Earlier than the in → next local day; only up to 12:00 AM (sent as next-day 00:00).
      const out = resolveOutMinutes(i, o);
      if (out.crossesMidnight) {
        setPunchErr(PUNCH_CROSSES_MIDNIGHT_MESSAGE);
        markBad('out');
        return;
      }
      outMinutes = out.minutes;
    }

    // 2020 required fields (legacy mode keeps today's In-only rule).
    if (mode === 'job') {
      if (!project) { markRequired('project', 'Pick a job'); return; }
      if (phases != null && phases.length === 0) {
        markRequired('phase', 'This job has no phases — add one in Sage');
        return;
      }
      if (phaseID == null) { markRequired('phase', 'Select a phase'); return; }
      if (accountCodeID == null) { markRequired('account', 'Select a cost code'); return; }
    } else if (mode === 'serviceOrder') {
      if (!pickedSO && !soNotInSync) { markRequired('serviceOrder', 'Choose a service order'); return; }
      if (soHasNoCodes) { markRequired('account', "This service order's job has no cost codes"); return; }
      if (accountCodeID == null) { markRequired('account', 'Select a cost code'); return; }
    }

    // Unmoved punch ends keep their original instant + offset verbatim.
    const inUnchanged = isEdit && origIn != null && i === origIn.minutes;
    const origOutMinutes = origOut != null && origIn != null ? rolled(origOut, origIn) : null;
    const outUnchanged = isEdit && outMinutes != null && origOutMinutes != null && outMinutes === origOutMinutes;

    const inStamp = inUnchanged
      ? { iso: entry!.row.inDateTime, offset: entry!.row.inDateTimeOffset }
      : wallTimeToTenantInstant(baseYmd, i);
    const outStamp = outMinutes == null
      ? null
      : outUnchanged
        ? { iso: entry!.row.outDateTime!, offset: entry!.row.outDateTimeOffset! }
        : wallTimeToTenantInstant(baseYmd, outMinutes);

    const body: PayrollHourlyEntryBody = {
      ...(isEdit ? {} : { employeeID, payPeriodID }),
      inDateTime: inStamp.iso,
      inDateTimeOffset: inStamp.offset,
      outDateTime: outStamp?.iso ?? null,
      outDateTimeOffset: outStamp?.offset ?? null,
      type,
      // 0 is the contract's "none" sentinel for the required ID fields (§8.1).
      projectID: project?.projectID ?? 0,
      accountCodeID: accountCodeID ?? 0,
      projectPhaseID: project ? phaseID ?? 0 : 0,
      customerServiceWarrantyID: entry?.group.customerServiceWarranty?.customerServiceWarrantyID ?? 0,
      serviceOrderText: entry?.group.serviceOrderText ?? '',
      serviceOrderID: entry?.row.serviceOrderID ?? null,
      // TAS-514 — round-tripped unchanged. Without it a payroll time correction on an NPM
      // service-order punch would silently unscope the punch, and an unscoped punch blocks
      // the week at submit and posts to Vista with no SMScope.
      serviceOrderScopeID: entry?.row.serviceOrderScopeID ?? entry?.group.serviceOrderScopeID ?? null,
      tempServiceOrderDescription: entry?.row.tempServiceOrderDescription ?? null,
      locationName: entry?.group.locationName ?? '',
      description: entry?.group.description ?? '',
      editSessionID,
    };
    // 2020 modes: same clocks / CSW / location, job costing from the mode.
    const saveBody: PayrollHourlyEntryBody = mode === null ? body : {
      ...body,
      ...modeFields(),
    };

    setSaving(true);
    setBanner(null);
    setPunchErr(null);
    const outcome = await performEntrySave('hourly', isEdit ? entry!.row.timeEntryHourlyID : null, saveBody);
    setSaving(false);
    if (!outcome.ok) {
      if (outcome.conflict) onConflict(outcome.message);
      else if (outcome.code === PUNCH_CROSSES_MIDNIGHT_CODE) { setPunchErr(outcome.message); setBad('out'); }
      else setBanner({ title: 'Could not save the hourly punch.', body: outcome.message });
      return;
    }
    onSaved(outcome.result, close, {
      mode, project, projectPhaseID: phaseID, accountCodeID, serviceOrder: pickedSO, type,
    });
  };

  // ── 2020 modes (clockHourlyV2Config) ────────────────────────────────────────
  const renderJobFields = () => (
    <>
      <div className="row2">
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <label>Job</label>
          <ProjectPicker
            value={project}
            onChange={(p) => { setProject(p); setPhaseID(null); clearBad('project'); }}
            bad={bad === 'project'}
          />
        </div>
      </div>
      {jobHasNoPhases && <WarnBanner title="This job has no phases — add one in Sage" />}
      <div className="row2">
        <div className="field">
          <label>Phase</label>
          <select
            className={`pe-in sans${bad === 'phase' ? ' bad' : ''}`}
            value={phaseID ?? ''}
            onChange={(ev) => {
              const id = ev.target.value === '' ? null : Number(ev.target.value);
              setPhaseID(id);
              clearBad('phase');
              const ph = (phases ?? []).find((p) => p.projectPhaseID === id);
              const codes = ph?.accountCodes ?? [];
              if (!codes.some((c) => c.accountCodeID === accountCodeID)) {
                setAccountCodeID(codes[0]?.accountCodeID ?? null);
              }
            }}
            disabled={!project || (phases ?? []).length === 0}
          >
            <option value="">{project ? ((phases ?? []).length ? 'Select phase…' : 'No phases') : 'Pick a job first'}</option>
            {(phases ?? []).map((p) => (
              <option key={p.projectPhaseID} value={p.projectPhaseID}>
                {p.altProjectPhaseID} · {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Cost code</label>
          <select
            className={`pe-in sans${bad === 'account' ? ' bad' : ''}`}
            value={accountCodeID ?? ''}
            onChange={(ev) => {
              setAccountCodeID(ev.target.value === '' ? null : Number(ev.target.value));
              clearBad('account');
            }}
            disabled={phase == null}
          >
            <option value="">Select cost code…</option>
            {jobAccountOptions.map((c) => (
              <option key={c.accountCodeID} value={c.accountCodeID}>{c.alt} · {c.name}</option>
            ))}
          </select>
        </div>
      </div>
    </>
  );

  const renderServiceOrderFields = () => (
    <>
      <div className="row2">
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <label>
            Service order
            {soJobClosed && <span className="pe-tag">Job closed — time edits only</span>}
          </label>
          {soNotInSync ? (
            <div className="pe-fixed">
              <span className="mono">SO #{entry?.row.serviceOrderID ?? ''}</span> — not in sync
            </div>
          ) : (
            <ServiceOrderPicker
              value={pickedSO}
              onChange={(so) => {
                setPickedSO(so);
                clearBad('serviceOrder');
                loadServiceOrder(so.serviceOrderID, false);
              }}
              disabled={soJobClosed}
              bad={bad === 'serviceOrder'}
            />
          )}
          {pickedSO && (
            <div className="pe-so-job">
              Job <span className="mono">{pickedSO.altProjectID ?? ''}</span> — {pickedSO.projectName ?? ''}
            </div>
          )}
        </div>
      </div>
      {soHasNoCodes && <WarnBanner title="This service order's job has no cost codes" />}
      <div className="row2">
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <label>Cost code</label>
          <select
            className={`pe-in sans${bad === 'account' ? ' bad' : ''}`}
            value={accountCodeID ?? ''}
            onChange={(ev) => {
              setAccountCodeID(ev.target.value === '' ? null : Number(ev.target.value));
              clearBad('account');
            }}
            disabled={soCodes == null || soCodes.length === 0}
          >
            <option value="">
              {soCodes == null
                ? (pickedSO || soNotInSync ? 'Loading cost codes…' : 'Choose a service order first')
                : 'Select cost code…'}
            </option>
            {(soCodes ?? []).map((c) => (
              <option key={c.accountCodeID} value={c.accountCodeID}>{c.alt} · {c.name}</option>
            ))}
          </select>
        </div>
      </div>
    </>
  );

  const renderModes = () => (
    <>
      {cfg.modes.length > 1 ? (
        <div className="pe-seg" role="tablist" aria-label="Hourly punch type">
          {cfg.modes.map((m) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={mode === m}
              className={mode === m ? 'on' : ''}
              onClick={() => switchMode(m)}
              disabled={saving}
            >
              {MODE_LABEL[m]}
            </button>
          ))}
        </div>
      ) : (
        <div className="pe-seg-caption">{mode ? MODE_LABEL[mode] : ''}</div>
      )}
      {mode === 'job' && orphanNonJob && project == null && (
        <WarnBanner title="This punch has no job" body="Pick the job it belongs to before saving." />
      )}
      {/* Every enabled mode's fields render in ONE grid cell; the inactive panes are
          visibility-hidden (not focusable). The stack is as tall as the tallest pane,
          so switching Job | Non-Job Time | Service Order never moves the clocks. */}
      <div className="pe-mode-stack">
        {cfg.modes.includes('job') && (
          <div className={`pe-mode-pane${mode === 'job' ? '' : ' off'}`} aria-hidden={mode !== 'job'}>
            {renderJobFields()}
          </div>
        )}
        {cfg.modes.includes('serviceOrder') && (
          <div className={`pe-mode-pane${mode === 'serviceOrder' ? '' : ' off'}`} aria-hidden={mode !== 'serviceOrder'}>
            {renderServiceOrderFields()}
          </div>
        )}
        {cfg.modes.includes('nonJob') && (
          <div className={`pe-mode-pane pe-mode-pane-note${mode === 'nonJob' ? '' : ' off'}`} aria-hidden={mode !== 'nonJob'}>
            {/* Locked & hidden by design: the account code is the tenant's configured one and
                never shown or chosen here (decision 2). */}
            <p className="pe-note">
              Non-Job Time — no job, phase or cost code. Booked to the company&rsquo;s non-job account.
            </p>
          </div>
        )}
      </div>
      <div className="row2">
        <div className="field">
          <label>Type</label>
          <select className="pe-in sans" value={type} onChange={(ev) => setType(Number(ev.target.value))}>
            <option value={TimeEntryType.Regular}>Regular</option>
            <option value={TimeEntryType.Meal}>Meal</option>
          </select>
        </div>
      </div>
      <div className="row2">
        <div className="field">
          <label>In</label>
          <input
            ref={inRef}
            className={`pe-in${bad === 'in' ? ' bad' : ''}`}
            value={inRaw}
            onChange={(ev) => { setInRaw(ev.target.value); setPunchErr(null); if (bad === 'in') setBad(null); }}
            placeholder="730"
            autoFocus
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div className="field">
          <label>Out</label>
          <input
            ref={outRef}
            className={`pe-in${bad === 'out' ? ' bad' : ''}`}
            value={outRaw}
            onChange={(ev) => { setOutRaw(ev.target.value); setPunchErr(null); if (bad === 'out') setBad(null); }}
            placeholder="1500"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </div>
      <PunchErrorSlot message={punchErr} />
    </>
  );

  return (
    <EntryModalShell
      kind="hourly"
      mode={isEdit ? 'edit' : 'add'}
      subtitle={`${ymdLabel(baseYmd)} · ${employeeName}`}
      banner={banner ? <EntryErrorBanner title={banner.title} body={banner.body} /> : null}
      echo={<PunchEchoParts inRaw={inRaw} outRaw={outRaw} />}
      saving={saving}
      onSave={handleSave}
      onCancel={onCancel}
    >
      {mode !== null ? renderModes() : (
      <>
      <div className="row2">
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <label>Project</label>
          <ProjectPicker
            value={project}
            onChange={(p) => { setProject(p); if (!p) setPhaseID(null); }}
            allowNone
            noneLabel="No job — bare hours"
          />
        </div>
      </div>
      <div className="row2">
        <div className="field">
          <label>Phase</label>
          <select
            className="pe-in sans"
            value={phaseID ?? ''}
            onChange={(ev) => {
              const id = ev.target.value === '' ? null : Number(ev.target.value);
              setPhaseID(id);
              const ph = (phases ?? []).find((p) => p.projectPhaseID === id);
              const codes = ph?.accountCodes ?? [];
              if (codes.length > 0 && !codes.some((c) => c.accountCodeID === accountCodeID)) {
                setAccountCodeID(codes[0].accountCodeID);
              }
            }}
            disabled={!project || (phases ?? []).length === 0}
          >
            <option value="">{project ? ((phases ?? []).length ? 'Select phase…' : 'No phases') : '— bare hours —'}</option>
            {(phases ?? []).map((p) => (
              <option key={p.projectPhaseID} value={p.projectPhaseID}>
                {p.altProjectPhaseID} · {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Account code</label>
          <select
            className="pe-in sans"
            value={accountCodeID ?? ''}
            onChange={(ev) => setAccountCodeID(ev.target.value === '' ? null : Number(ev.target.value))}
          >
            <option value="">Select account…</option>
            {accountOptions.map((c) => (
              <option key={c.accountCodeID} value={c.accountCodeID}>{c.alt} · {c.name}</option>
            ))}
          </select>
        </div>
      </div>
      {serviceOrder && (
        <div className="row2">
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Service order</label>
            <div className="pe-so-readonly">
              <div className="pe-so-line">
                <span className="pe-so-num">{serviceOrder.number}</span>
                {serviceOrder.name && <span className="pe-so-name">{serviceOrder.name}</span>}
              </div>
              {serviceOrder.scope && <div className="pe-so-scope">{serviceOrder.scope}</div>}
              {serviceOrder.state && <div className="pe-so-state">{serviceOrder.state}</div>}
              <div className="pe-so-note">
                Read only — the employee resolves a service order in the mobile app. Time
                edits here keep the punch's service order and scope.
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="row2">
        <div className="field">
          <label>Type</label>
          <select className="pe-in sans" value={type} onChange={(ev) => setType(Number(ev.target.value))}>
            <option value={TimeEntryType.Regular}>Regular</option>
            <option value={TimeEntryType.Meal}>Meal</option>
          </select>
        </div>
      </div>
      <div className="row2">
        <div className="field">
          <label>In</label>
          <input
            ref={inRef}
            className={`pe-in${bad === 'in' ? ' bad' : ''}`}
            value={inRaw}
            onChange={(ev) => { setInRaw(ev.target.value); setPunchErr(null); if (bad === 'in') setBad(null); }}
            placeholder="730"
            autoFocus
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div className="field">
          <label>Out</label>
          <input
            ref={outRef}
            className={`pe-in${bad === 'out' ? ' bad' : ''}`}
            value={outRaw}
            onChange={(ev) => { setOutRaw(ev.target.value); setPunchErr(null); if (bad === 'out') setBad(null); }}
            placeholder="1500"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </div>
      <PunchErrorSlot message={punchErr} />
      </>
      )}
    </EntryModalShell>
  );
};

/** In-modal warning — the EntryErrorBanner frame in the amber `.pe-ban.warn` tone. */
const WarnBanner: React.FC<{ title: string; body?: React.ReactNode }> = ({ title, body }) => (
  <div className="pe-ban warn">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
    <div className="grow">
      <b>{title}</b>
      {body}
    </div>
  </div>
);

function isHourlyMode(shape: HourlyRowShape | undefined): shape is HourlyMode {
  return shape === 'job' || shape === 'nonJob' || shape === 'serviceOrder';
}

/** One option per accountCodeID (an SO job's phases share codes). */
function dedupeCodes(codes: CodeOption[]): CodeOption[] {
  const seen = new Set<number>();
  return codes.filter((c) => {
    if (seen.has(c.accountCodeID)) return false;
    seen.add(c.accountCodeID);
    return true;
  });
}

/** `7:30a` text for prefilled clock inputs (round-trips through parseClock). */
function clockText(minutes: number): string {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  const h24 = Math.floor(m / 60);
  const mi = m % 60;
  const ap = h24 >= 12 ? 'p' : 'a';
  let h = h24 % 12;
  if (h === 0) h = 12;
  return `${h}:${String(mi).padStart(2, '0')}${ap}`;
}

/** Out-punch minutes with the overnight day-rollover applied relative to the in date. */
function rolled(
  out: { ymd: string; minutes: number },
  inn: { ymd: string; minutes: number } | null
): number {
  if (!inn) return out.minutes;
  return out.ymd > inn.ymd ? out.minutes + 1440 : out.minutes;
}

export default HourlyEntryModal;
